import { useEffect, useState } from "react";
import { getTrainingGpuClusterUsage, type TrainingGpuClusterUsage } from "@/api/trainingGpu";
import type { LoadState } from "@/components/dataset/loadState";
import { createLogger } from "@/logging";
import { gpuUsageWindow, type GpuUsagePeriod } from "./gpuUsagePeriod";

const logger = createLogger("components/gpu/usage");

type GpuClusterUsageController = {
  state: LoadState<TrainingGpuClusterUsage>;
  isLoading: boolean;
};

/**
 * 기간 내 클러스터 점유 추세를 조회한다. 띠를 둔 화면의 다른 조회와 따로 읽는다. 그래서 이
 * 조회가 실패해도 화면의 나머지는 그린다.
 *
 * 폴링하지 않는다. 일 버킷 집계는 초 단위로 바뀌지 않는다. `reloadToken` 이 바뀌면 다시 조회한다.
 * 사용자가 누른 새로고침이 이 값을 올린다.
 *
 * 다시 조회하는 동안 직전 데이터를 유지한다. 기간을 바꿀 때마다 띠가 비었다가 다시 채워지면
 * 화면 높이가 흔들린다. 요청 중 상태는 별도로 반환해 새로고침 버튼이 중복 요청을 막게 한다.
 */
export function useGpuClusterUsage(
  period: GpuUsagePeriod,
  reloadToken: number,
): GpuClusterUsageController {
  const [state, setState] = useState<LoadState<TrainingGpuClusterUsage>>({ status: "idle" });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    async function load(): Promise<void> {
      setIsLoading(true);
      setState((previous) => (previous.status === "success" ? previous : { status: "loading" }));
      try {
        const data = await getTrainingGpuClusterUsage(gpuUsageWindow(period, Date.now()), {
          signal,
        });
        if (!signal.aborted) {
          setState({ status: "success", data });
        }
      } catch (error) {
        if (signal.aborted) return;
        logger.error("Failed to load cluster GPU usage.", error);
        setState({
          status: "error",
          message: "클러스터 점유 추세를 불러오지 못했습니다. 잠시 후 새로고침해 주세요.",
        });
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    }
    void load();
    return () => {
      controller.abort();
    };
  }, [period, reloadToken]);

  return { state, isLoading };
}
